import React, { useEffect, useRef, useState } from 'react';
import {
  FiCalendar,
  FiChevronDown,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RANGE_OPTIONS } from './dateRange';
import { PMS_OPTIONS } from './derive';

const HOST_LIFECYCLE_OPTIONS = [
  { id: 'all', label: 'All hosts' },
  { id: 'new', label: 'New hosts' },
  { id: 'legacy', label: 'Legacy hosts' },
];

const ORDER_STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'created', label: 'Created' },
  { id: 'paymentCompleted', label: 'Payment completed' },
  { id: 'requested', label: 'Requested' },
  { id: 'pending', label: 'Pending' },
  { id: 'picked', label: 'Picked' },
  { id: 'completed', label: 'Completed' },
];

const DELIVERY_TYPE_OPTIONS = [
  { id: 'all', label: 'All delivery types' },
  { id: 'deliver_to_door', label: 'Deliver to door' },
  { id: 'pre_arrival_stocking', label: 'Pre-arrival stocking' },
  { id: 'pre_arrival_stocking_plus_door', label: 'Stocking + door' },
];

const US_STATE_OPTIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
].map((code) => ({ id: code, label: code }));

const STATE_OPTIONS = [{ id: 'all', label: 'All states' }, ...US_STATE_OPTIONS];

const SEARCH_PLACEHOLDERS = {
  growth: 'Search hosts by name, email, company, phone',
  pms: 'Search hosts by name, email, company, phone',
  hosts: 'Search hosts by name, email, company, phone',
  drivers: 'Search drivers by name, email, market',
  orders: 'Search orders by ID, store, property, host',
  properties: 'Search properties by address, city, host',
  map: 'Search orders by ID, store, property, host',
};

function useClickAway(ref, onAway) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      onAway();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onAway, ref]);
}

function CompactSelect({ value, onChange, options, label, widthClass = 'w-48' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickAway(ref, () => setOpen(false));
  const current = options.find((o) => o.id === value) || options[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-900">{current.label}</span>
        <FiChevronDown className="h-3.5 w-3.5 text-zinc-400" />
      </button>
      {open ? (
        <div className={`absolute right-0 z-30 mt-1 max-h-72 overflow-auto rounded-lg border border-zinc-200 bg-white shadow-lg ${widthClass}`}>
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-zinc-50 ${
                opt.id === value ? 'bg-indigo-50/50 text-indigo-700' : 'text-zinc-700'
              }`}
            >
              {opt.label}
              {opt.id === value ? <span className="text-indigo-500">●</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function DashboardFilterBar({
  activeView,
  filters,
  onChange,
  onReset,
  onRefresh,
  refreshing,
}) {
  const [searchInput, setSearchInput] = useState(filters.search);
  const [storeInput, setStoreInput] = useState(filters.storeLocation);
  const [moreOpen, setMoreOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const moreRef = useRef(null);
  const customRef = useRef(null);

  useClickAway(moreRef, () => setMoreOpen(false));
  useClickAway(customRef, () => setCustomOpen(false));

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setStoreInput(filters.storeLocation);
  }, [filters.storeLocation]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) onChange({ search: searchInput });
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.search, onChange, searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (storeInput !== filters.storeLocation) onChange({ storeLocation: storeInput });
    }, 250);
    return () => clearTimeout(timer);
  }, [filters.storeLocation, onChange, storeInput]);

  const hasOrderAdvancedFilters =
    filters.orderStatus !== 'all' ||
    filters.deliveryFeeType !== 'all' ||
    Boolean(filters.storeLocation);

  const hasActiveFilters =
    filters.range !== '30d' ||
    filters.pms !== 'all' ||
    filters.hostLifecycle !== 'all' ||
    filters.state !== 'all' ||
    filters.orderStatus !== 'all' ||
    filters.deliveryFeeType !== 'all' ||
    Boolean(filters.storeLocation) ||
    Boolean(filters.search);

  const searchPlaceholder = SEARCH_PLACEHOLDERS[activeView] || SEARCH_PLACEHOLDERS.hosts;
  const showOrderControls = activeView === 'orders' || activeView === 'map';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
        {RANGE_OPTIONS.map((opt) => {
          const active = filters.range === opt.id;
          const isCustom = opt.id === 'custom';
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange({ range: opt.id });
                if (isCustom) setCustomOpen(true);
              }}
              title={opt.longLabel}
              className={`h-8 rounded-md px-2.5 text-xs font-medium transition ${
                active ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {filters.range === 'custom' ? (
        <div className="relative" ref={customRef}>
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <FiCalendar className="h-3.5 w-3.5 text-zinc-400" />
            {filters.customStart && filters.customEnd
              ? `${new Date(filters.customStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(filters.customEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
              : 'Pick range'}
          </button>
          {customOpen ? (
            <div className="absolute left-0 z-40 mt-1 rounded-lg border border-zinc-200 bg-white p-3 shadow-lg">
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-medium text-zinc-600">Start</label>
                <DatePicker
                  selected={filters.customStart}
                  onChange={(d) => onChange({ customStart: d })}
                  selectsStart
                  startDate={filters.customStart}
                  endDate={filters.customEnd}
                  className="h-8 w-44 rounded-md border border-zinc-200 px-2 text-xs"
                />
                <label className="text-[11px] font-medium text-zinc-600">End</label>
                <DatePicker
                  selected={filters.customEnd}
                  onChange={(d) => onChange({ customEnd: d })}
                  selectsEnd
                  startDate={filters.customStart}
                  endDate={filters.customEnd}
                  minDate={filters.customStart}
                  className="h-8 w-44 rounded-md border border-zinc-200 px-2 text-xs"
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <CompactSelect
        label="PMS"
        value={filters.pms}
        onChange={(value) => onChange({ pms: value })}
        options={PMS_OPTIONS}
      />

      <CompactSelect
        label="Lifecycle"
        value={filters.hostLifecycle}
        onChange={(value) => onChange({ hostLifecycle: value })}
        options={HOST_LIFECYCLE_OPTIONS}
      />

      <CompactSelect
        label="State"
        value={filters.state}
        onChange={(value) => onChange({ state: value })}
        options={STATE_OPTIONS}
        widthClass="w-28"
      />

      <div className="relative min-w-[220px] flex-1">
        <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 w-full rounded-lg border border-zinc-200 bg-white pl-8 pr-3 text-xs text-zinc-900 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
        />
      </div>

      {showOrderControls ? (
        <div className="relative" ref={moreRef}>
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <FiFilter className="h-3.5 w-3.5 text-zinc-400" />
            Order filters
            {hasOrderAdvancedFilters ? (
              <span className="rounded-full bg-zinc-900 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {[
                  filters.orderStatus !== 'all',
                  filters.deliveryFeeType !== 'all',
                  Boolean(filters.storeLocation),
                ].filter(Boolean).length}
              </span>
            ) : null}
            <FiChevronDown className="h-3.5 w-3.5 text-zinc-400" />
          </button>
          {moreOpen ? (
            <div className="absolute right-0 z-30 mt-1 w-80 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg">
              <div className="space-y-4 p-3">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    Order status
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {ORDER_STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => onChange({ orderStatus: opt.id })}
                        className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                          filters.orderStatus === opt.id
                            ? 'bg-zinc-900 text-white'
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    Delivery type
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {DELIVERY_TYPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => onChange({ deliveryFeeType: opt.id })}
                        className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                          filters.deliveryFeeType === opt.id
                            ? 'bg-zinc-900 text-white'
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    Store
                  </p>
                  <input
                    type="search"
                    value={storeInput}
                    onChange={(e) => setStoreInput(e.target.value)}
                    placeholder="Search store location"
                    className="h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-xs text-zinc-900 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {hasActiveFilters ? (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          <FiX className="h-3.5 w-3.5 text-zinc-400" />
          Reset
        </button>
      ) : null}

      <button
        type="button"
        onClick={onRefresh}
        className="inline-flex h-9 items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
      >
        <FiRefreshCw className={`h-3.5 w-3.5 text-zinc-400 ${refreshing ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );
}
